import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import {
  Newspaper,
  Users,
  EnvelopeSimple,
  ChartLine,
  Plus,
  Pencil,
  Trash,
  ArrowsClockwise,
  Check,
  X,
  CaretDown,
  House
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, token, isAdmin, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [users, setUsers] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === "overview") {
        const response = await axios.get(`${API}/admin/stats`, { headers });
        setStats(response.data);
      } else if (activeTab === "articles") {
        const response = await axios.get(`${API}/admin/articles`, { headers });
        setArticles(response.data.articles);
      } else if (activeTab === "users") {
        const response = await axios.get(`${API}/admin/users`, { headers });
        setUsers(response.data.users);
      } else if (activeTab === "subscribers") {
        const response = await axios.get(`${API}/admin/subscribers`, { headers });
        setSubscribers(response.data.subscribers);
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [activeTab, headers]);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [isAdmin, authLoading, fetchData]);

  const handleScrape = async () => {
    try {
      await axios.post(`${API}/admin/scrape`, {}, { headers });
      toast.success("Scraping started - new articles will appear soon");
    } catch (error) {
      toast.error("Failed to start scraping");
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm("Are you sure you want to delete this article?")) return;
    
    try {
      await axios.delete(`${API}/admin/articles/${articleId}`, { headers });
      setArticles(articles.filter(a => a.id !== articleId));
      toast.success("Article deleted");
    } catch (error) {
      toast.error("Failed to delete article");
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FFF1E5] flex items-center justify-center">
        <div className="animate-pulse text-[#6B6661]">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#FFF1E5] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-[#33302E] mb-4">Access Denied</h1>
          <p className="text-[#6B6661] mb-6">You need admin privileges to access this page.</p>
          <Link to="/" className="px-6 py-3 bg-[#111827] text-white font-sans">
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview", icon: ChartLine },
    { id: "articles", label: "Articles", icon: Newspaper },
    { id: "users", label: "Users", icon: Users },
    { id: "subscribers", label: "Subscribers", icon: EnvelopeSimple },
  ];

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      {/* Admin Header */}
      <header className="bg-[#111827] text-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <House size={20} />
                <span className="font-sans text-sm">Back to Site</span>
              </Link>
              <span className="text-white/30">|</span>
              <span className="font-serif text-lg">Farmwatch Admin</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-white/70">{user?.email}</span>
              <button
                onClick={handleScrape}
                data-testid="admin-scrape-btn"
                className="flex items-center gap-2 px-4 py-2 bg-[#0F766E] text-white text-sm hover:bg-[#0D9488] transition-colors"
              >
                <ArrowsClockwise size={16} />
                Scrape News
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-[#E0D4C8]">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              data-testid={`admin-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 font-sans text-sm transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? "border-[#0F766E] text-[#0F766E]"
                  : "border-transparent text-[#6B6661] hover:text-[#33302E]"
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-pulse text-[#6B6661]">Loading...</div>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === "overview" && stats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white border border-[#E0D4C8] p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Newspaper size={24} className="text-[#0F766E]" />
                    <span className="text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                      Articles
                    </span>
                  </div>
                  <p className="font-mono text-3xl text-[#33302E]">{stats.total_articles}</p>
                </div>
                <div className="bg-white border border-[#E0D4C8] p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Users size={24} className="text-[#0F766E]" />
                    <span className="text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                      Users
                    </span>
                  </div>
                  <p className="font-mono text-3xl text-[#33302E]">{stats.total_users}</p>
                </div>
                <div className="bg-white border border-[#E0D4C8] p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <EnvelopeSimple size={24} className="text-[#0F766E]" />
                    <span className="text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                      Subscribers
                    </span>
                  </div>
                  <p className="font-mono text-3xl text-[#33302E]">{stats.total_subscribers}</p>
                </div>
                <div className="bg-white border border-[#E0D4C8] p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <ChartLine size={24} className="text-[#0F766E]" />
                    <span className="text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                      Regions
                    </span>
                  </div>
                  <div className="space-y-1 mt-2">
                    {Object.entries(stats.articles_by_region || {}).map(([region, count]) => (
                      <div key={region} className="flex justify-between text-sm">
                        <span className="text-[#6B6661] capitalize">{region}</span>
                        <span className="font-mono text-[#33302E]">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Articles Tab */}
            {activeTab === "articles" && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-serif text-xl text-[#33302E]">Manage Articles</h2>
                  <button
                    onClick={() => setShowCreateForm(true)}
                    data-testid="admin-create-article-btn"
                    className="flex items-center gap-2 px-4 py-2 bg-[#111827] text-white text-sm hover:bg-[#0F766E] transition-colors"
                  >
                    <Plus size={16} />
                    New Article
                  </button>
                </div>

                {showCreateForm && (
                  <ArticleForm
                    onClose={() => setShowCreateForm(false)}
                    onSuccess={() => {
                      setShowCreateForm(false);
                      fetchData();
                    }}
                    token={token}
                  />
                )}

                <div className="bg-white border border-[#E0D4C8]">
                  <table className="w-full">
                    <thead className="bg-[#F6E9DD]">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                          Title
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                          Region
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                          Category
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                          Source
                        </th>
                        <th className="text-right px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E7EB]">
                      {articles.map((article) => (
                        <tr key={article.id} className="hover:bg-[#F9FAFB]">
                          <td className="px-4 py-3">
                            <p className="font-sans text-sm text-[#33302E] line-clamp-1">
                              {article.title}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 bg-[#E5E7EB] text-[#6B6661] text-xs font-mono uppercase">
                              {article.region}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-[#6B6661] capitalize">
                              {article.category}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-[#6B6661]">{article.source}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => setEditingArticle(article)}
                                className="p-2 text-[#6B6661] hover:text-[#0F766E] transition-colors"
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteArticle(article.id)}
                                className="p-2 text-[#6B6661] hover:text-[#DC2626] transition-colors"
                              >
                                <Trash size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === "users" && (
              <div className="bg-white border border-[#E0D4C8]">
                <table className="w-full">
                  <thead className="bg-[#F6E9DD]">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                        Name
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                        Email
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                        Role
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                        Joined
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB]">
                    {users.map((u) => (
                      <tr key={u.id} className="hover:bg-[#F9FAFB]">
                        <td className="px-4 py-3 font-sans text-sm text-[#33302E]">{u.name}</td>
                        <td className="px-4 py-3 font-sans text-sm text-[#6B6661]">{u.email}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs font-mono uppercase ${
                            u.role === "admin" ? "bg-[#0F766E] text-white" : "bg-[#E5E7EB] text-[#6B6661]"
                          }`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-sm text-[#6B6661]">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Subscribers Tab */}
            {activeTab === "subscribers" && (
              <div className="bg-white border border-[#E0D4C8]">
                <table className="w-full">
                  <thead className="bg-[#F6E9DD]">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                        Email
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                        Name
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                        Regions
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">
                        Subscribed
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB]">
                    {subscribers.map((sub) => (
                      <tr key={sub.id} className="hover:bg-[#F9FAFB]">
                        <td className="px-4 py-3 font-sans text-sm text-[#33302E]">{sub.email}</td>
                        <td className="px-4 py-3 font-sans text-sm text-[#6B6661]">{sub.name || "-"}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 flex-wrap">
                            {sub.regions?.map((r) => (
                              <span key={r} className="px-2 py-0.5 bg-[#E5E7EB] text-[#6B6661] text-xs capitalize">
                                {r}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-sm text-[#6B6661]">
                          {new Date(sub.subscribed_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ArticleForm({ onClose, onSuccess, token, article = null }) {
  const [formData, setFormData] = useState({
    title: article?.title || "",
    summary: article?.summary || "",
    content: article?.content || "",
    source: article?.source || "Farmwatch",
    source_url: article?.source_url || "",
    image_url: article?.image_url || "",
    region: article?.region || "ireland",
    category: article?.category || "general",
    is_featured: article?.is_featured || false,
    is_published: article?.is_published ?? true
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (article) {
        await axios.put(`${API}/admin/articles/${article.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/admin/articles`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Article created");
      }
      onSuccess();
    } catch (error) {
      toast.error("Failed to save article");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-[#E0D4C8] p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-lg text-[#33302E]">
          {article ? "Edit Article" : "Create New Article"}
        </h3>
        <button onClick={onClose} className="p-2 text-[#6B6661] hover:text-[#33302E]">
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-[#6B6661] mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="w-full px-4 py-2 border border-[#E0D4C8] font-sans text-sm focus:outline-none focus:border-[#0F766E]"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-[#6B6661] mb-2">
                Region
              </label>
              <select
                value={formData.region}
                onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                className="w-full px-4 py-2 border border-[#E0D4C8] font-sans text-sm focus:outline-none focus:border-[#0F766E]"
              >
                <option value="ireland">Ireland</option>
                <option value="uk">UK</option>
                <option value="usa">USA</option>
                <option value="china">China</option>
                <option value="india">India</option>
                <option value="brazil">Brazil</option>
                <option value="global">Global</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-[#6B6661] mb-2">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 border border-[#E0D4C8] font-sans text-sm focus:outline-none focus:border-[#0F766E]"
              >
                <option value="general">General</option>
                <option value="dairy">Dairy</option>
                <option value="livestock">Livestock</option>
                <option value="crops">Crops</option>
                <option value="machinery">Machinery</option>
                <option value="markets">Markets</option>
                <option value="policy">Policy</option>
                <option value="agri-tech">Agri-Tech</option>
              </select>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-xs font-mono uppercase tracking-wider text-[#6B6661] mb-2">
            Summary *
          </label>
          <textarea
            value={formData.summary}
            onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
            required
            rows={2}
            className="w-full px-4 py-2 border border-[#E0D4C8] font-sans text-sm focus:outline-none focus:border-[#0F766E]"
          />
        </div>

        <div>
          <label className="block text-xs font-mono uppercase tracking-wider text-[#6B6661] mb-2">
            Content *
          </label>
          <textarea
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            required
            rows={4}
            className="w-full px-4 py-2 border border-[#E0D4C8] font-sans text-sm focus:outline-none focus:border-[#0F766E]"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-[#6B6661] mb-2">
              Image URL
            </label>
            <input
              type="url"
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              className="w-full px-4 py-2 border border-[#E0D4C8] font-sans text-sm focus:outline-none focus:border-[#0F766E]"
            />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-[#6B6661] mb-2">
              Source URL
            </label>
            <input
              type="url"
              value={formData.source_url}
              onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
              className="w-full px-4 py-2 border border-[#E0D4C8] font-sans text-sm focus:outline-none focus:border-[#0F766E]"
            />
          </div>
        </div>

        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_featured}
              onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
              className="w-4 h-4"
            />
            <span className="text-sm text-[#33302E]">Featured</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_published}
              onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
              className="w-4 h-4"
            />
            <span className="text-sm text-[#33302E]">Published</span>
          </label>
        </div>

        <div className="flex gap-4">
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-[#111827] text-white font-sans text-sm hover:bg-[#0F766E] transition-colors disabled:opacity-50"
          >
            {loading ? "Saving..." : article ? "Update Article" : "Create Article"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-[#E0D4C8] text-[#6B6661] font-sans text-sm hover:border-[#111827] transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
